/* ==========================================================================
   DOYENNE THEME JS
   1. NAV CONTROLLER — ported from the Onyx nav (onyx-nav.js): open/close
      state sync and cart count sizing. The drawer itself is a static row
      of image tiles (see header.liquid), so no panel logic is needed.
   2. SITE HELPERS — scroll reveal, play-on-visible videos, FAQ accordions,
      horizontal sliders. All vanilla JS, no jQuery/Swiper dependencies.
   ========================================================================== */

(function () {
  var selectors = {
    cartCount: '[data-doyenne-nav-cart-count]',
    cartCountValue: '[data-cart-render="item_count"]',
    menu: '[data-doyenne-nav-menu]',
    searchInput: '[data-doyenne-nav-search-input]',
    searchToggle: '[data-doyenne-nav-search-toggle]',
    toggle: '[data-doyenne-nav-toggle]',
  };

  /* ----- NAV OPEN / CLOSE CONTROLLER ----- */
  function getNavMenu(root) {
    var header = root.closest('.doyenne-header');
    var headerSibling = header ? header.nextElementSibling : null;

    if (headerSibling && headerSibling.matches(selectors.menu)) {
      return headerSibling;
    }
    return document.querySelector(selectors.menu);
  }

  function isNavOpen(root, menu) {
    return root.classList.contains('open') || Boolean(menu && menu.classList.contains('open'));
  }

  function setNavOpen(root, menu, isOpen) {
    var menuToggle = root.querySelector(selectors.toggle);

    root.classList.toggle('open', isOpen);

    if (menu) {
      menu.classList.toggle('open', isOpen);
    }
    if (menuToggle) {
      menuToggle.classList.toggle('active', isOpen);
      menuToggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    }
  }

  function focusSearchInput(root) {
    var searchInput = root.querySelector(selectors.searchInput);
    if (!searchInput) return;
    try {
      searchInput.focus({ preventScroll: true });
    } catch (error) {
      searchInput.focus();
    }
  }

  function initNavOpenToggle(root) {
    var header = root.closest('.doyenne-header') || root.closest('header');
    var menu = getNavMenu(root);
    var toggles = root.querySelectorAll([selectors.searchToggle, selectors.toggle].join(','));

    setNavOpen(root, menu, isNavOpen(root, menu));

    toggles.forEach(function (toggle) {
      toggle.addEventListener('click', function () {
        var shouldOpen = !isNavOpen(root, menu);
        setNavOpen(root, menu, shouldOpen);
        if (shouldOpen && toggle.matches(selectors.searchToggle)) {
          focusSearchInput(root);
        }
      });
    });

    document.addEventListener('click', function (event) {
      var isProtected =
        root.contains(event.target) ||
        Boolean(menu && menu.contains(event.target)) ||
        Boolean(header && header.contains(event.target));

      if (!isNavOpen(root, menu) || isProtected) return;
      setNavOpen(root, menu, false);
    });

    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Escape' || !isNavOpen(root, menu)) return;
      setNavOpen(root, menu, false);
    });
  }

  /* ----- CART COUNT ----- */
  function syncCartCount(cartCount) {
    var countValue = cartCount.querySelector(selectors.cartCountValue);
    var count = String((countValue || cartCount).textContent || '0').replace(/[^0-9]/g, '');
    var quantity = count.length ? parseInt(count, 10) : 0;

    cartCount.style.setProperty('--cart-count-ratio', count.length >= 3 ? '1.5 / 1' : '1 / 1');
    cartCount.style.display = quantity > 0 ? '' : 'none';
  }

  function initCartCount(root) {
    var cartCount = root.querySelector(selectors.cartCount);
    if (!cartCount) return;

    syncCartCount(cartCount);

    if (!window.MutationObserver) return;

    var countValue = cartCount.querySelector(selectors.cartCountValue) || cartCount;
    new MutationObserver(function () {
      syncCartCount(cartCount);
    }).observe(countValue, { childList: true, characterData: true, subtree: true });
  }

  /* ----- SITE HELPERS ----- */

  // SCROLL REVEAL: [data-reveal] elements gain .revealed on first view.
  function initReveal(scope) {
    var elements = (scope || document).querySelectorAll('[data-reveal]:not(.revealed)');
    if (!elements.length) return;

    if (!('IntersectionObserver' in window)) {
      elements.forEach(function (el) { el.classList.add('revealed'); });
      return;
    }

    var observer = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('revealed');
          observer.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px' });

    elements.forEach(function (el) { observer.observe(el); });
  }

  // Start MP4 requests shortly before the media enters view, then play only
  // while visible. Desktop tabs load only their active clip.
  var videoVisibility = new WeakMap();
  var videoNearby = new WeakMap();
  var observedVideos = new WeakSet();
  var videoBreakpoint = window.matchMedia('(max-width: 900px)');
  var videoObserver;
  var videoPrefetchObserver;

  function syncVideo(video) {
    var tab = video.closest('[data-trans-tab], [data-test-tab]');
    var active = !tab || videoBreakpoint.matches || tab.classList.contains('active');
    if (active && !document.hidden && (videoNearby.get(video) || videoVisibility.get(video))) {
      var source = video.querySelector('source[data-src]');
      if (source) {
        source.src = source.dataset.src;
        source.removeAttribute('data-src');
        video.preload = 'auto';
        video.load();
        if (videoPrefetchObserver) videoPrefetchObserver.unobserve(video);
      }
    }
    if (!videoVisibility.get(video) || !active || document.hidden) {
      video.pause();
      return;
    }

    var playing = video.play();
    if (playing && playing.catch) playing.catch(function () {});
  }

  function syncVideos(scope) {
    (scope || document).querySelectorAll('video[data-play-on-visible]').forEach(syncVideo);
  }
  window.doyenneSyncVideos = syncVideos;

  function initVideos(scope) {
    var videos = (scope || document).querySelectorAll('video[data-play-on-visible]');
    if (!videos.length) return;

    if (!('IntersectionObserver' in window)) {
      videos.forEach(function (video) {
        videoVisibility.set(video, true);
        syncVideo(video);
      });
      return;
    }

    if (!videoObserver) {
      videoObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          videoVisibility.set(entry.target, entry.isIntersecting);
          syncVideo(entry.target);
        });
      }, { threshold: 0.1 });
      videoPrefetchObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          videoNearby.set(entry.target, entry.isIntersecting);
          if (entry.isIntersecting) syncVideo(entry.target);
        });
      }, { rootMargin: '600px 0px' });

      if (videoBreakpoint.addEventListener) {
        videoBreakpoint.addEventListener('change', function () { syncVideos(); });
      } else if (videoBreakpoint.addListener) {
        videoBreakpoint.addListener(function () { syncVideos(); });
      }
      document.addEventListener('visibilitychange', function () { syncVideos(); });
    }

    videos.forEach(function (video) {
      if (observedVideos.has(video)) return;
      observedVideos.add(video);
      videoObserver.observe(video);
      videoPrefetchObserver.observe(video);
    });
  }

  // Wistia's swatch is tiny. Fetch its full thumbnail only shortly before a
  // custom click-to-play card enters view; the player still waits for a click.
  var wistiaThumbnailObserver;
  var observedWistiaThumbnails = new WeakSet();
  var wistiaThumbnailRequest = 0;

  function loadWistiaThumbnail(image) {
    var id = image.getAttribute('data-wistia-thumbnail');
    if (!id || !/^[a-z0-9]+$/i.test(id)) return;

    var callbackName = '__doyenneWistiaThumbnail' + (++wistiaThumbnailRequest);
    var script = document.createElement('script');
    function cleanup() {
      delete window[callbackName];
      script.remove();
    }
    window[callbackName] = function (data) {
      if (data && /^https:\/\//.test(data.thumbnail_url || '')) {
        image.src = data.thumbnail_url;
      }
      cleanup();
    };
    script.onerror = cleanup;
    script.onload = cleanup;
    script.src = 'https://fast.wistia.com/oembed.json?url=' +
      encodeURIComponent('https://home.wistia.com/medias/' + id) +
      '&callback=' + callbackName;
    document.head.appendChild(script);
  }

  function initWistiaThumbnails(scope) {
    var images = (scope || document).querySelectorAll('img[data-wistia-thumbnail]');
    if (!images.length) return;
    if (!('IntersectionObserver' in window)) {
      images.forEach(loadWistiaThumbnail);
      return;
    }

    if (!wistiaThumbnailObserver) {
      wistiaThumbnailObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          wistiaThumbnailObserver.unobserve(entry.target);
          loadWistiaThumbnail(entry.target);
        });
      }, { rootMargin: '500px 0px' });
    }
    images.forEach(function (image) {
      if (observedWistiaThumbnails.has(image)) return;
      observedWistiaThumbnails.add(image);
      wistiaThumbnailObserver.observe(image);
    });
  }

  // FAQ ACCORDION: [data-faq-toggle] toggles its sibling answer.
  function initFaqs(scope) {
    (scope || document).querySelectorAll('[data-faq-toggle]').forEach(function (toggle) {
      if (toggle.dataset.faqBound) return;
      toggle.dataset.faqBound = 'true';

      toggle.addEventListener('click', function () {
        var item = toggle.closest('.a-faq');
        var answer = item ? item.querySelector('.faq-answer') : null;
        if (!item || !answer) return;

        var isOpen = item.classList.toggle('active');
        toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        answer.style.maxHeight = isOpen ? answer.scrollHeight + 'px' : '';
      });
    });
  }

  // LOCATION SEARCH: [data-location-search] filters its sibling
  // [data-location-list] entries by text as the user types.
  function initLocationSearch(scope) {
    (scope || document).querySelectorAll('[data-location-search]').forEach(function (input) {
      if (input.dataset.searchBound) return;
      input.dataset.searchBound = 'true';

      var list = input.parentElement.querySelector('[data-location-list]');
      if (!list) return;

      input.addEventListener('input', function () {
        var query = input.value.trim().toLowerCase();
        list.querySelectorAll('.location').forEach(function (item) {
          var match = query === '' || item.textContent.toLowerCase().indexOf(query) !== -1;
          item.classList.toggle('is-hidden', !match);
        });
      });
    });
  }

  // SWIPER: [data-swiper] containers become free-mode, drag-to-scroll
  // sliders with CSS-sized slides. Gap comes from data-swiper-space.
  function initSwipers(scope) {
    if (typeof window.Swiper === 'undefined') return;

    (scope || document).querySelectorAll('[data-swiper]').forEach(function (el) {
      if (el.swiper) return;

      new window.Swiper(el, {
        slidesPerView: 'auto',
        spaceBetween: parseInt(el.dataset.swiperSpace || '0', 10),
        freeMode: { enabled: true, momentumBounce: false },
        grabCursor: true,
        watchOverflow: true
      });
    });
  }

  function initSite(scope) {
    initReveal(scope);
    initVideos(scope);
    initWistiaThumbnails(scope);
    initFaqs(scope);
    initSwipers(scope);
    initLocationSearch(scope);
  }

  /* ----- HORSE LOGO -----
     The header logo is a canvas playing 18 WebP frames ripped from the
     galloping-horse gif. Scrolling scrubs the gallop: scroll distance in
     EITHER direction accumulates, so the horse only ever runs forward. */
  function initHorseLogo() {
    document.querySelectorAll('canvas[data-horse-logo]').forEach(function (canvas) {
      var urls;
      try {
        urls = JSON.parse(canvas.getAttribute('data-frames') || '[]');
      } catch (error) {
        urls = [];
      }
      if (!urls.length || !canvas.getContext) return;

      var ctx = canvas.getContext('2d');
      var frames = new Array(urls.length);
      var current = -1;

      var SCROLL_PER_FRAME = 24; // px of scroll travel per frame (~430px per gallop cycle)
      var distance = 0;
      var lastY = window.pageYOffset;
      var ticking = false;

      var reduceMotion =
        window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      function draw(index) {
        if (index === current || !frames[index]) return;
        current = index;
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(frames[index], 0, 0, canvas.width, canvas.height);
      }

      function onScroll() {
        var y = window.pageYOffset;
        distance += Math.abs(y - lastY); // forward-only: |delta| always advances
        lastY = y;
        if (ticking) return;
        ticking = true;
        window.requestAnimationFrame(function () {
          ticking = false;
          draw(Math.floor(distance / SCROLL_PER_FRAME) % frames.length);
        });
      }

      urls.forEach(function (url, index) {
        var img = new Image();
        img.decoding = 'async';
        img.onload = function () {
          frames[index] = img;
          if (index === 0) {
            draw(0);
            if (!reduceMotion) window.addEventListener('scroll', onScroll, { passive: true });
          }
        };
        img.src = url;
      });
    });
  }

  /* ----- QUICK ADD -----
     Collection-grid quick-add forms post to the AJAX cart so the shopper
     stays on the page; the header cart count updates in place. */
  function initQuickAdd() {
    document.addEventListener('submit', function (event) {
      var form = event.target.closest('.quick-add-form');
      if (!form || !window.fetch) return;
      event.preventDefault();

      var button = form.querySelector('[type="submit"]');
      if (button) button.disabled = true;

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form)
      })
        .then(function (response) {
          if (!response.ok) throw new Error('Add to cart failed');
          return fetch('/cart.js');
        })
        .then(function (response) { return response.json(); })
        .then(function (cart) {
          document.querySelectorAll(selectors.cartCountValue).forEach(function (el) {
            el.textContent = cart.item_count;
          });
        })
        .catch(function () {
          // AJAX add failed (e.g. sold out variant) — fall back to the PDP.
          if (form.action) window.location.href = form.action;
        })
        .finally(function () {
          if (button) button.disabled = false;
        });
    });
  }

  function initAll() {
    document.querySelectorAll('[data-doyenne-nav]').forEach(function (root) {
      initCartCount(root);
      initNavOpenToggle(root);
    });
    initHorseLogo();
    initQuickAdd();
    initSite(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Re-init inside sections when edited in the theme customizer.
  document.addEventListener('shopify:section:load', function (event) {
    initHorseLogo();
    initSite(event.target);
  });
})();
